'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useRoomContext } from '@livekit/components-react';

export type Platform = 'youtube' | 'instagram';

interface StreamContextType {
  // Estado: Guarda o ID do Egress de cada plataforma (se null, está offline)
  activeStreams: Record<Platform, string | null>; 
  isLoading: Record<Platform, boolean>;
  
  startStream: (platform: Platform, url: string, key: string) => Promise<void>;
  stopStream: (platform: Platform) => Promise<void>;
  
  toggleStreamModal: () => void;
  isStreamModalOpen: boolean;
  isHost: boolean;
}

export const StreamContext = createContext<StreamContextType | undefined>(undefined);

export function StreamProvider({ children, isHost }: { children: React.ReactNode, isHost: boolean }) {
  const room = useRoomContext();
  const [isStreamModalOpen, setIsStreamModalOpen] = useState(false);
  
  // Estado individual por plataforma
  const [activeStreams, setActiveStreams] = useState<Record<Platform, string | null>>({
    youtube: null,
    instagram: null
  });
  
  const [isLoading, setIsLoading] = useState<Record<Platform, boolean>>({
    youtube: false,
    instagram: false
  });

  const startStream = useCallback(async (platform: Platform, url: string, key: string) => {
    if (!room || !isHost) return;
    
    setIsLoading(prev => ({ ...prev, [platform]: true }));

    try {
      // Enviamos apenas UM output por vez para ter IDs separados
      const output = { url, key };

      const resp = await fetch('/api/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName: room.name,
          outputs: [output] // Array de 1 item
        })
      });

      const data = await resp.json();
      
      if (data.success) {
        setActiveStreams(prev => ({ ...prev, [platform]: data.egressId }));
      } else {
        alert(`Erro ao iniciar ${platform}: ` + data.error);
      }
    } catch (error) {
      console.error(error);
      alert('Erro de conexão ao tentar iniciar stream.');
    } finally {
      setIsLoading(prev => ({ ...prev, [platform]: false }));
    }
  }, [room, isHost]);

  const stopStream = useCallback(async (platform: Platform) => {
    const egressId = activeStreams[platform];
    if (!egressId) return;

    setIsLoading(prev => ({ ...prev, [platform]: true }));

    try {
      await fetch('/api/stream', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ egressId })
      });
      
      setActiveStreams(prev => ({ ...prev, [platform]: null }));
    } catch (error) {
      console.error(error);
      alert("Erro ao parar stream.");
    } finally {
      setIsLoading(prev => ({ ...prev, [platform]: false }));
    }
  }, [activeStreams]);

  return (
    <StreamContext.Provider value={{
      activeStreams, isLoading, isHost,
      startStream, stopStream,
      toggleStreamModal: () => setIsStreamModalOpen(!isStreamModalOpen),
      isStreamModalOpen
    }}>
      {children}
    </StreamContext.Provider>
  );
}

export const useStream = () => {
  const ctx = useContext(StreamContext);
  if (!ctx) throw new Error("useStream must be used within StreamProvider");
  return ctx;
};